import { useState } from "react";
import { Flag, X, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`}>{children}</span>
);
const inp = "bg-navy-950 border border-gold-500/25 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-gold-500/60 w-full";

export const shrinkImage = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, 1400 / img.width);
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * scale);
    c.height = Math.round(img.height * scale);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    resolve(c.toDataURL("image/jpeg", 0.72));
  };
  img.onerror = reject;
  img.src = URL.createObjectURL(file);
});

export function RaiseModal({ contextTitle, submit, onDone, onClose }) {
  const [note, setNote] = useState("");
  const [image, setImage] = useState(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (f) setImage(await shrinkImage(f));
  };
  const send = async () => {
    if (!note.trim()) return toast.error("Say a few words about what's blocking you.");
    setBusy(true);
    try {
      await submit(note.trim(), image);
      toast.success("Raised to the administrator — it's on their board now.");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't raise the flag.");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Raise to administrator" data-testid="flag-modal">
      <div className="absolute inset-0 bg-navy-950/85" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-navy-900 border border-gold-500/30 w-full max-w-md p-5 space-y-4 text-white">
        <div className="flex items-center justify-between">
          <Mono className="text-gold-400 text-[0.55rem] inline-flex items-center gap-2"><Flag className="h-3 w-3" /> Raise to administrator</Mono>
          <button onClick={onClose} data-testid="flag-close" aria-label="Close"><X className="h-4 w-4 text-white/50" /></button>
        </div>
        <p className="text-white/60 text-xs font-light">"{contextTitle}" — what's blocking you? A few words is enough. This goes straight onto the administrator's board as a priority.</p>
        <textarea rows={3} autoFocus value={note} onChange={(e) => setNote(e.target.value)} data-testid="flag-note-input"
          placeholder="e.g. Ledger line 14 has an on-cost I can't verify" className={inp} />
        <div className="flex items-center gap-3 flex-wrap">
          <label className="inline-flex items-center gap-2 border border-gold-500/25 px-3 py-2 text-xs text-white/60 hover:text-gold-300 cursor-pointer">
            <ImageIcon className="h-3.5 w-3.5" /> {image ? "Change screenshot" : "Attach screenshot (optional)"}
            <input type="file" accept="image/*" className="hidden" onChange={pick} data-testid="flag-image-input" />
          </label>
          {image && <img src={image} alt="screenshot" className="h-12 border border-gold-500/30" data-testid="flag-image-preview" />}
        </div>
        <div className="flex gap-2">
          <button onClick={send} disabled={busy} data-testid="flag-send-btn"
            className="flex-1 bg-gold-500 text-navy-950 font-semibold text-xs py-2.5 hover:bg-gold-400 disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Send to administrator
          </button>
          <button onClick={onClose} className="border border-gold-500/25 text-white/60 text-xs px-4 hover:text-white">Cancel</button>
        </div>
      </div>
    </div>
  );
}

export function RaiseButton({ contextTitle, className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="raise-to-admin-btn"
        className={`inline-flex items-center gap-1.5 text-red-300/70 hover:text-red-300 text-xs ${className}`}>
        <Flag className="h-3.5 w-3.5" /> Raise an issue
      </button>
      {open && (
        <RaiseModal contextTitle={contextTitle} onClose={() => setOpen(false)} onDone={() => setOpen(false)}
          submit={(note, image) => api.post("/tasks/raise", { context: contextTitle, note, image_b64: image })} />
      )}
    </>
  );
}
